import { ReactElement, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { getPlaneFromShape } from '@editorUtils'
import {
  DrawControllerWrapper,
  Label,
  useTapelineCentersSnapTargets,
  useTapelineSnapTargets,
} from '@scene'
import { find } from 'lodash-es'
import { Vector3 } from 'three'
import { v4 as uuid } from 'uuid'
import {
  DraggableRectangle,
  DraggableRectangleRef,
  DrawControllerRef,
  ImmutableVector3,
  RectangleXYZ,
  TransientDrawState,
  toImmutable,
} from '@modugen/ui-toolkit'
import { useEditElementStore, useControlStore, useModelStore } from '@editorStores'
import { useClickActionEnabled } from '@editorHooks'
import { useStructuralPlanningDrawerEsc } from '@structuralPlanningHooks'

const minOpeningSize = 0.5

interface Props {
  wallGuid: string
  openingGuid: string | null
}

const OpeningsDrawer = ({ wallGuid, openingGuid }: Props): ReactElement => {
  const draggableRectangleRef = useRef<DraggableRectangleRef>(null)
  const drawControllerRef = useRef<DrawControllerRef>(null)

  const setSnappingFeaturesEnabled = useControlStore(state => state.setSnappingFeaturesEnabled)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)

  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)

  const { walls } = useModelStore(state => state.model)
  const addOpening = useModelStore(state => state.addOpening)

  const tapelineTargets = useTapelineSnapTargets()
  const tapelineCenterTargets = useTapelineCentersSnapTargets()
  const snapTargets = useMemo(
    () => [...tapelineTargets, ...tapelineCenterTargets],
    [tapelineTargets, tapelineCenterTargets],
  )

  const [isDrawing, setIsDrawing] = useState(false)

  // MEMOS

  const wall = useMemo(() => find(walls, { guid: wallGuid }), [wallGuid, walls]) as ShapeObject

  const wallShape = wall?.shape.points as RectangleXYZ

  const wallWidthDirection = useMemo(() => wallShape[1].sub(wallShape[0]).normalize(), [wallShape])
  const wallHeightDirection = useMemo(() => new ImmutableVector3(0, 0, 1), [wallShape])

  // EFFECTS

  useLayoutEffect(() => {
    setIsDrawingActive(true)

    return () => {
      setIsDrawingActive(false)
    }
  }, [setIsDrawingActive])

  useEffect(() => {
    setSnappingFeaturesEnabled({
      snapOrthogonal: true,
      snapToAngle: true,
      snapToCornersAndEdges: true,
      snapToTapelineCenters: true,
      snapToComments: false,
    })
    return () => {
      setSnappingFeaturesEnabled({
        snapOrthogonal: true,
        snapToCornersAndEdges: true,
        snapToAngle: true,
        snapToTapelineCenters: true,
        snapToComments: false,
      })
    }
  })

  // HOTKEY

  useStructuralPlanningDrawerEsc(() => {
    if (isDrawing) {
      drawControllerRef.current?.abortDrawing()
      if (draggableRectangleRef.current) {
        draggableRectangleRef.current.stopDraggingAndGetPoints()

        // not so nice way to reset the draggable rectangle to it's defaults

        const defaultPoint = new ImmutableVector3(0, 0, -1000)
        draggableRectangleRef.current.setHandleAndStartDragging('Start', defaultPoint)
        draggableRectangleRef.current.stopDraggingAndGetPoints()
        draggableRectangleRef.current.setHandleAndStartDragging('End', defaultPoint)
        draggableRectangleRef.current.stopDraggingAndGetPoints()
      }
      setIsDrawing(false)
    } else if (openingGuid) {
      setActiveOpening(null)
    }
  }, isDrawing || !!openingGuid)

  // EVENTS

  const onDrawStart = (state: TransientDrawState) => {
    const { drawPoint } = state

    if (!drawPoint) return

    draggableRectangleRef.current?.setHandleAndStartDragging('End', state.drawPoint)
    setIsDrawing(true)
  }

  const onDrawMouseMove = (state: TransientDrawState) => {
    const { drawPoint } = state

    if (!drawPoint || !draggableRectangleRef.current?.activeHandle) return

    draggableRectangleRef.current?.updateActiveHandle(
      drawPoint,
      wallWidthDirection,
      wallHeightDirection,
    )
  }

  const onDrawEnd = () => {
    setIsDrawing(false)

    if (!draggableRectangleRef.current?.activeHandle) return

    const points = draggableRectangleRef.current?.stopDraggingAndGetPoints() as RectangleXYZ

    const size = points[0].distanceTo(points[2])

    const wallPlane = getPlaneFromShape(wallShape)

    const projectedOpeningPoints = points.map(p =>
      toImmutable(wallPlane.projectPoint(p.v, new Vector3())),
    )

    if (size > minOpeningSize) {
      const id = uuid()
      const newOpening: Opening = {
        guid: id,
        shape: {
          guid: id,
          points: projectedOpeningPoints,
        },
        is_local: true,
      }

      addOpening(wall.guid, newOpening)
      setActiveOpening(id)
    }
  }

  const [labelStart, labelEnd, wallLength] = useMemo(() => {
    const wallStart = wall.shape.points[0]
    const wallEnd = wall.shape.points[1]

    // const wallDirection = wallStart.directionTo(wallEnd)

    const zDirection = new ImmutableVector3(0, 0, 1)

    const labelStart = wallStart.addScaledVector(zDirection, -0.5)
    const labelEnd = wallEnd.addScaledVector(zDirection, -0.5)

    const wallLength = wallStart.distanceTo(wallEnd)

    return [labelStart, labelEnd, wallLength]
  }, [wall])

  const drawControllerEnabled = useClickActionEnabled()

  return (
    <>
      <DrawControllerWrapper
        ref={drawControllerRef}
        enabled={drawControllerEnabled}
        enableIndicator
        onDrawStart={onDrawStart}
        onMouseMove={onDrawMouseMove}
        onDrawEnd={onDrawEnd}
        additionalSnapTargets={snapTargets}
        snapToCornersAndEdges={snapToCornersAndEdges}
        isValidDrawTarget={object => object.userData.type === 'wall'}
      />

      <DraggableRectangle
        color="blue"
        ref={draggableRectangleRef}
        points={[
          new ImmutableVector3(0, 0, -1000),
          new ImmutableVector3(0, 0, -1000),
          new ImmutableVector3(0, 0, -1000),
          new ImmutableVector3(0, 0, -1000),
        ]}
        rectangleProps={{
          nonSelectable: true,
        }}
      />

      <Label
        label="0.00m"
        position={labelStart.v}
        visible
        htmlProps={{
          center: true,
        }}
      />

      <Label
        label={`${wallLength.toFixed(2)}m`}
        position={labelEnd.v}
        visible
        htmlProps={{
          center: true,
        }}
      />
    </>
  )
}

export default OpeningsDrawer
